import { Platform } from 'react-native';

/* eslint-disable no-undef */
const analyticsHitApi = {
  hits: {},
  duplicateCalls: {},
  lastCallTimes: {},
  requestDetails: {},
  catalogApiHits: 0,
  cartApiHits: 0,
  otherApiHits: 0,
  duplicateThreshold: 2000, // 2 seconds

  // Track API hit
  track(url, params = null, payload = null, method = 'GET') {
    const now = Date.now();
    const endpointKey = this._getEndpointKey(url);

    if (!this.hits[endpointKey]) {
      this.hits[endpointKey] = 0;
      this.duplicateCalls[endpointKey] = 0;
      this.requestDetails[endpointKey] = [];
    }

    // Create a request signature for comparison
    const requestSignature = this._createRequestSignature(
      params,
      payload,
      method,
    );

    // Store request details
    const requestDetail = {
      timestamp: now,
      params: params || 'none',
      payload: payload || 'none',
      signature: requestSignature,
    };

    // Check for duplicate calls with same signature
    const isDuplicate = this._isDuplicateRequest(endpointKey, requestSignature);

    // Store last 5 request details to prevent memory growth
    this.requestDetails[endpointKey].unshift(requestDetail);
    if (this.requestDetails[endpointKey].length > 5) {
      this.requestDetails[endpointKey].pop();
    }

    if (isDuplicate) {
      this.duplicateCalls[endpointKey]++;
      this._warnDuplicateCall(url, endpointKey);
    }

    // Update tracking information
    this.lastCallTimes[endpointKey] = now;
    this.hits[endpointKey]++;

    // Track by endpoint type
    if (url.includes('ms-catalog')) {
      this.catalogApiHits++;
    } else if (url.includes('ms-cart')) {
      this.cartApiHits++;
    } else {
      this.otherApiHits++;
    }

    return this.hits[endpointKey];
  },

  // Create a unique signature for a request based on its parameters and payload
  _createRequestSignature(params, payload, method = 'GET') {
    try {
      const paramsStr = params ? JSON.stringify(params) : '';
      const payloadStr = payload ? JSON.stringify(payload) : '';
      return `${(method || 'GET').toUpperCase()}|${paramsStr}|${payloadStr}`;
    } catch (e) {
      // If JSON stringification fails, use string representations
      return `${(method || 'GET').toUpperCase()}|${String(
        params || '',
      )}|${String(payload || '')}`;
    }
  },

  // Check if this is a duplicate request based on signature and time threshold
  _isDuplicateRequest(endpointKey, currentSignature) {
    const now = Date.now();
    const recentRequests = this.requestDetails[endpointKey] || [];

    // Look for recent identical requests within the threshold
    for (const request of recentRequests) {
      if (
        request.signature === currentSignature &&
        now - request.timestamp < this.duplicateThreshold
      ) {
        return true;
      }
    }

    return false;
  },

  // Normalize URL to a consistent endpoint key (strip domain, keep path and query)
  _getEndpointKey(rawUrl) {
    if (!rawUrl) {
      return '';
    }

    try {
      const parsed = new URL(rawUrl, 'https://lilla.id');
      const path = parsed.pathname.replace(/\/+$|^$/, '') || '/';
      return `${path}${parsed.search || ''}`;
    } catch (e) {
      return rawUrl;
    }
  },

  // Warn about duplicate API calls
  _warnDuplicateCall(url, endpointKey) {
    if (__DEV__) {
      // Get calling stack trace to identify source
      const stackTrace = new Error().stack;
      const callingComponent = this._extractCallingComponent(stackTrace);

      // Get the recent request details for this URL
      const currentRequest = this.requestDetails[endpointKey][0] || {};
      const previousRequest = this.requestDetails[endpointKey][1] || {};

      // Format params and payload for display
      const formatData = (data) => {
        try {
          return typeof data === 'string'
            ? data
            : JSON.stringify(data, null, 2);
        } catch (e) {
          return String(data);
        }
      };

      if (Platform.OS === 'web') {
        // eslint-disable-next-line no-console
        console.warn(
          `⚠️ Duplicate API Call Detected, Please Check ⚠️\n` +
            `Endpoint: ${url}\n` +
            `Total duplicates: ${this.duplicateCalls[endpointKey]}\n` +
            `Time between calls: <${this.duplicateThreshold}ms\n` +
            `Current request params: ${formatData(currentRequest.params)}\n` +
            `Current request payload: ${formatData(currentRequest.payload)}\n` +
            `Previous request params: ${formatData(previousRequest.params)}\n` +
            `Previous request payload: ${formatData(
              previousRequest.payload,
            )}\n` +
            (callingComponent
              ? `Potential source: ${callingComponent}\n`
              : '') +
            `Consider optimizing or caching this request.`,
        );
      } else {
        // eslint-disable-next-line no-console
        console.warn(
          `⚠️ Duplicate API Call Detected, Please Check ⚠️\n` +
            `Endpoint: ${url}\n` +
            `Total duplicates: ${this.duplicateCalls[endpointKey]}\n`,
        );
      }
    }
  },

  // Extract calling component from stack trace
  _extractCallingComponent(stackTrace) {
    if (!stackTrace) {
      return null;
    }

    const lines = stackTrace.split('\n');
    const relevantLines = [];

    // Skip first two lines which are the error and this method
    for (let i = 2; i < Math.min(lines.length, 8); i++) {
      const line = lines[i];
      if (
        line &&
        !line.includes('analytics-hit-api.js') &&
        !line.includes('interceptors.js')
      ) {
        const fileMatch = line.match(/\((.*):(\d+):(\d+)\)$/);
        if (fileMatch) {
          const filePath = fileMatch[1];
          const lineNumber = fileMatch[2];

          const fileName = filePath.split('/').pop();

          let functionName = line
            .substring(0, line.indexOf('('))
            .trim()
            .replace(/^at /, '');

          if (functionName.includes('.')) {
            functionName = functionName.split('.').pop();
          }

          relevantLines.push(`${functionName} (${fileName}:${lineNumber})`);
        } else {
          relevantLines.push(line.trim().replace(/^at /, ''));
        }
      }
    }

    return relevantLines.length > 0 ? relevantLines.join('\n  ↳ ') : null;
  },

  // Get hit count for specific endpoint
  getHitCount(url) {
    const endpointKey = this._getEndpointKey(url);
    return this.hits[endpointKey] || 0;
  },

  // Get duplicate calls for specific endpoint
  getDuplicateCount(url) {
    const endpointKey = this._getEndpointKey(url);
    return this.duplicateCalls[endpointKey] || 0;
  },

  // Get request details for specific endpoint
  getRequestDetails(url) {
    const endpointKey = this._getEndpointKey(url);
    return this.requestDetails[endpointKey] || [];
  },

  // Get all hit statistics
  getStats() {
    return {
      endpointHits: this.hits,
      duplicateCalls: this.duplicateCalls,
      catalogApiHits: this.catalogApiHits,
      cartApiHits: this.cartApiHits,
      otherApiHits: this.otherApiHits,
      totalHits: this.catalogApiHits + this.cartApiHits + this.otherApiHits,
      totalDuplicates: Object.values(this.duplicateCalls).reduce(
        (sum, count) => sum + count,
        0,
      ),
    };
  },

  // Log current statistics and return stats object
  logStats() {
    const stats = this.getStats();

    if (__DEV__) {
      // eslint-disable-next-line no-console
      console.debug('API Hit Statistics:', stats);

      // Find endpoints with high duplicate counts
      const highDuplicates = Object.entries(this.duplicateCalls)
        .filter(([_, count]) => count > 2)
        .sort(([_1, countA], [_2, countB]) => countB - countA);

      if (highDuplicates.length > 0) {
        // eslint-disable-next-line no-console
        console.warn(
          'ℹ️ Endpoints with high duplicate calls:',
          highDuplicates.map(([url, count]) => `${url} (${count} duplicates)`),
        );
      }
    }

    return stats;
  },

  // Configure duplicate threshold
  setDuplicateThreshold(ms) {
    this.duplicateThreshold = ms;
    return this; // Enable method chaining
  },

  // Reset counters
  reset() {
    this.hits = {};
    this.duplicateCalls = {};
    this.lastCallTimes = {};
    this.requestDetails = {};
    this.catalogApiHits = 0;
    this.cartApiHits = 0;
    this.otherApiHits = 0;
    return this; // Enable method chaining
  },
};

export default analyticsHitApi;
